import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { INBOUND_HANDLERS, parseRawInbound } from '../../api/inbound/inbound';
import { isConfigFlagEnabled } from '../../config';
import { setConnectionIsLost } from '../../redux/actions/sessionActions';
import { useAppDispatch } from '../../redux/store';
import { useReduxStore } from '../../redux/useReduxStore';
import { useInstanceId } from '../instance/instanceContext';
import { useReloadInstance } from '../reloadInstance/reloadInstanceContext';
import { getWebSocketManager } from './webSocketCache';
const defaultWebSocketContextProps = {
    socketSend: () => {
        throw new Error('WebSocket Context not provided');
    },
    closeSocket: () => {
        throw new Error('WebSocket Context not provided');
    }
};
function getIsAutoReconnectEnabled() {
    const params = new URLSearchParams(window.location.search);
    const autoReconnectParam = params.get('autoReconnect');
    if (autoReconnectParam === 'on') {
        return true;
    }
    if (autoReconnectParam === 'off') {
        return false;
    }
    return isConfigFlagEnabled('autoReconnect');
}
const IS_AUTO_RECONNECT_ENABLED = getIsAutoReconnectEnabled();
export const WebSocketContext = createContext(defaultWebSocketContextProps);
export const WebSocketProvider = ({ children, existingWebSocketManager }) => {
    const instanceId = useInstanceId();
    const reloadInstance = useReloadInstance();
    const store = useReduxStore();
    const dispatch = useAppDispatch();
    const onSocketMessage = useCallback((rawInbound, socketSend) => {
        const parseResults = parseRawInbound(rawInbound, instanceId);
        parseResults.forEach((parseResult) => {
            if (!parseResult || !parseResult.inbound) {
                return;
            }
            const { inbound } = parseResult;
            const handler = INBOUND_HANDLERS[inbound._type];
            handler(inbound, store, socketSend);
        });
    }, [instanceId, store]);
    const onSocketClosed = useCallback(() => {
        // TODO: check if entry scene?
        const shouldAutoReconnect = IS_AUTO_RECONNECT_ENABLED;
        if (shouldAutoReconnect) {
            reloadInstance();
        }
        else {
            dispatch(setConnectionIsLost());
        }
    }, [dispatch, reloadInstance]);
    const [webSocketManager, setWebSocketManager] = useState(existingWebSocketManager || getWebSocketManager(instanceId, onSocketMessage, onSocketClosed));
    useEffect(() => {
        webSocketManager.setOnSocketMessage(onSocketMessage);
    }, [onSocketMessage, webSocketManager]);
    useEffect(() => {
        webSocketManager.setOnSocketClosed(onSocketClosed);
    }, [onSocketClosed, webSocketManager]);
    useEffect(() => {
        if (existingWebSocketManager) {
            setWebSocketManager(existingWebSocketManager);
        }
    }, [existingWebSocketManager]);
    const value = useMemo(() => ({
        webSocketManager,
        socketSend: webSocketManager.socketSend.bind(webSocketManager),
        closeSocket: webSocketManager.closeSocket.bind(webSocketManager)
    }), [webSocketManager]);
    return _jsx(WebSocketContext.Provider, { value: value, children: children });
};
export function useSocketSend() {
    const { socketSend } = useContext(WebSocketContext);
    return socketSend;
}
export function useCloseSocket() {
    const { closeSocket } = useContext(WebSocketContext);
    return closeSocket;
}
